import { useState, useEffect } from 'react';

interface DearFlipState {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useDearFlip = (): DearFlipState & { loadDearFlip: () => Promise<boolean> } => {
  const [state, setState] = useState<DearFlipState>({
    isLoaded: false,
    isLoading: false,
    error: null,
  });

  // DearFlip이 이미 로드되어 있는지 확인
  const isDearFlipLoaded = (): boolean => {
    return !!(window as any).DFBook && !!(window as any).$;
  };

  // CSS 로드 함수
  const loadDearFlipCSS = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // 이미 로드되어 있는지 확인
      const existingLink = document.querySelector('link[href*="dearflip-jquery"]');
      if (existingLink) {
        resolve();
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = 'https://cdn.jsdelivr.net/npm/dearflip-jquery@2.0.12/dist/df-book.min.css';
      
      link.onload = () => resolve();
      link.onerror = () => reject(new Error('DearFlip CSS 로드 실패'));
      
      document.head.appendChild(link);
    });
  };

  // JavaScript 로드 함수
  const loadDearFlipJS = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // 이미 로드되어 있는지 확인
      const existingScript = document.querySelector('script[src*="dearflip-jquery"]');
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/dearflip-jquery@2.0.12/dist/df-book.min.js';
      script.async = true;
      
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('DearFlip JS 로드 실패'));
      
      document.head.appendChild(script);
    });
  };

  // jQuery 로드 함수 (DearFlip이 jQuery에 의존함)
  const loadjQuery = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // 이미 로드되어 있는지 확인
      if ((window as any).$) {
        resolve();
        return;
      }

      const existingScript = document.querySelector('script[src*="jquery"]');
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jquery/3.7.1/jquery.min.js';
      script.async = true;
      
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('jQuery 로드 실패'));
      
      document.head.appendChild(script);
    });
  };

  // DearFlip 로드 함수
  const loadDearFlip = async (): Promise<boolean> => {
    if (state.isLoaded || isDearFlipLoaded()) {
      return true;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // jQuery 먼저 로드
      await loadjQuery();
      
      // CSS 로드
      await loadDearFlipCSS();
      
      // JavaScript 로드
      await loadDearFlipJS();

      // DearFlip이 완전히 초기화될 때까지 대기
      await new Promise<void>((resolve) => {
        const checkDearFlip = () => {
          if ((window as any).DFBook) {
            resolve();
          } else {
            setTimeout(checkDearFlip, 100);
          }
        };
        checkDearFlip();
      });

      setState({
        isLoaded: true,
        isLoading: false,
        error: null,
      });

      console.log('DearFlip 로드 성공');
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'DearFlip 로드 실패';
      setState({
        isLoaded: false,
        isLoading: false,
        error: errorMessage,
      });
      console.error('DearFlip 로드 실패:', error);
      return false;
    }
  };

  // 컴포넌트 마운트 시 이미 로드되어 있는지 확인
  useEffect(() => {
    if (isDearFlipLoaded()) {
      setState(prev => ({ ...prev, isLoaded: true }));
    }
  }, []);

  return {
    ...state,
    loadDearFlip,
  };
};
